import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { GridConfig } from '@inavista/components/grid';

// Shipment order popup → Details → Items on @inavista/components. Same columns, labels, order, default visibility,
// widths, alignment, links (Part Order, Item, Description) and toolbar (Columns, Search, Excel export for
// AllGrid_Toolbar_Excel_Export, Clear filters) as the Syncfusion grid it replaces. Rows are the popup's own items
// (not deleted, as the old Query), served by LocalRowsGridApi. Read only. No paging (Martin).

export const SHIPMENT_DETAILS_ITEMS_URL = 'page://shipment-details-items';
export const SHIPMENT_DETAILS_ITEMS_PERSISTENCE_KEY = 'rims.service.shipment-order-details-items';

export const SHIPMENT_DETAILS_ITEMS_COLUMNS: [string, string][] = [
  ['__rowKey', 'int'],
  ['partsOrderRef', 'nvarchar'],
  ['warehouseOrderLN', 'nvarchar'],
  ['warehouseOrderLNLine', 'decimal'],
  ['warehouseOrderLNSequence', 'decimal'],
  ['serviceOrderId', 'nvarchar'],
  ['serviceOrderIdLN', 'nvarchar'],
  ['itemCode', 'nvarchar'],
  ['itemDescription', 'nvarchar'],
  ['itemGroup', 'nvarchar'],
  ['orderQty', 'decimal'],
  ['quantityShippedInInventoryUnit', 'decimal'],
];

const SHOWN = SHIPMENT_DETAILS_ITEMS_COLUMNS.map(([c]) => c).filter(c => c !== '__rowKey');
const RIGHT = ['orderQty', 'quantityShippedInInventoryUnit'];

export function buildShipmentDetailsItemsGridConfig(canExport: boolean): GridConfig {
  return {
    apiBaseUrl: SHIPMENT_DETAILS_ITEMS_URL,
    clientSide: true,
    keyColumn: '__rowKey',
    rowHeight: 30,
    defaultColumnOrder: SHOWN,
    hiddenByDefaultColumns: ['__rowKey'],
    startHiddenColumns: ['warehouseOrderLNLine', 'warehouseOrderLNSequence', 'serviceOrderId', 'itemGroup'],
    columnLabels: {
      partsOrderRef: 'Part Order', warehouseOrderLN: 'LN Warehouse Order No', warehouseOrderLNLine: 'LN WH Line#',
      warehouseOrderLNSequence: 'LN WH Line Seq.', serviceOrderId: 'Service Order No.', serviceOrderIdLN: 'LN Service Order No.',
      itemCode: 'Item', itemDescription: 'Description', itemGroup: 'Item Group', orderQty: 'Ord Qty',
      quantityShippedInInventoryUnit: 'Ship Qty',
    },
    // The old alignment: quantities right, the rest left; headers left.
    columnAlign: Object.fromEntries(SHOWN.map(c => [c, {
      horizontal: RIGHT.includes(c) ? 'right' as const : 'left' as const, header: { horizontal: 'left' as const },
    }])),
    // The old widths; Description had none and took the space left, so it is the only one that stretches.
    columnWidths: {
      partsOrderRef: 100, warehouseOrderLN: 130, warehouseOrderLNLine: 130, warehouseOrderLNSequence: 130, serviceOrderId: 120,
      serviceOrderIdLN: 120, itemCode: 130, itemDescription: 200, itemGroup: 150, orderQty: 157, quantityShippedInInventoryUnit: 157,
    },
    stretchColumns: true,
    columnSizing: Object.fromEntries(SHOWN.filter(c => c !== 'itemDescription').map(c => [c, { noStretch: true }])),
    // Syncfusion's 'n': grouped, up to 3 decimals.
    columnNumberFormats: Object.fromEntries(['warehouseOrderLNLine', 'warehouseOrderLNSequence', 'orderQty', 'quantityShippedInInventoryUnit'].map(c => [c, { maximumFractionDigits: 3 }])),
    hyperlinkColumns: ['partsOrderRef', 'itemCode', 'itemDescription'],
    // Item and Item Group had the Excel (checkbox list) filter; the rest the menu filter.
    excelFilterColumns: ['itemCode', 'itemGroup'],
    enableColumnsPicker: true,
    enableExport: canExport,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    multiSort: true,
    emptyStateMessage: 'No records to display',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: SHIPMENT_DETAILS_ITEMS_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
